import React, { useRef, useState, ReactNode } from "react";
import CreateContext, { ContextType } from "./create-context";
import {
  CodeViewEdge,
  CodeViewNode,
} from "../app/components/new-canvas/canvas.component";
import { Node } from "../app/components/new-canvas/InfraStructure";
import { RepoUpdateStatus, DiffSummary, GraphChanges, CodeRescanMode, RepoScanCommitInfo } from "../common/api";
import { Input } from "../app/components/new-view-edit-architecture/components/AddMultipleGitRepoModal/AddMultipleGitRepoModal";
import { ChatMessage } from "../types/chat";
import type { V2CanvasViewMode } from "../app/components/canvas-v2/v2SidebarData";

interface ContextProviderProps {
  children: ReactNode;
}

const ContextProvider: React.FC<ContextProviderProps> = (props) => {
  /**
   * states
   */
  const [theme, setTheme] = useState<string>("light");
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  /*For Top Navbar Active View*/
  const [activeSection, setActiveSection] = useState<string>("");
  /*canvas*/
  const [highlightEdgesContext, setHighlightEdgesContext] = useState<{
    key: any;
    nodes: any[];
    clearPrevious?: boolean;
  }>({
    key: null,
    nodes: [],
  }); /*For highlighting edges path*/
  const [highlightNodesContext, setHighlightNodesContext] = useState<any[]>(
    [],
  ); /*For highlighting nodes*/
  /*For Searching Node Name in Analysis Pane*/
  const [searchNode, setSearchNode] = useState<any>(null);
  //For Navbar,Leftbar and Footer Bar Toggle
  const [showBars, setShowBars] = useState<{
    showNavbar: boolean;
    showLeftSidebar: boolean;
    showFooter: boolean;
  }>({
    showNavbar: true,
    showLeftSidebar: true,
    showFooter: true,
  });
  //set footer input text field
  const [footerSearchText, setFooterSearchText] = useState<string>("");

  //project list state
  const [projects, setProjects] = useState<any[]>([]);

  //set active button for footerbar
  const [footerBtn, setFooterBtn] = useState<any>(null);

  // set the side bar active and deactive in canvas
  const [showDocSidebarCanvas, setDocSidebarCanvas] = useState<boolean>(false);

  //Handlers
  /**
   * Toggle Theme
   */
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  /**
   * Spinner Handler
   */
  const openSpinnerHandler = () => {
    setShowSpinner(true);
  };

  const closeSpinnerHandler = () => {
    setShowSpinner(false);
  };

  /**
   * To use gptcache or not in the backend is driven by this state
   */
  const [useCache, setUseCache] = useState<boolean>(true);
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
  const [edgeColor, setEdgeColor] = useState<any>();
  const [zoomVal, setZoomVal] = useState<any>();
  const [userId, setUserId] = useState<any>();
  const [queryStr, setQueryStr] = useState<string>("");
  const [selectedInfraviewDetail, setSelectedInfraviewDetail] = useState<any[]>(
    [],
  );
  const [showToastr, setShowToastr] = useState<{
    show: boolean;
    type: "error" | "success" | "info";
    text: React.ReactNode;
  }>({
    show: false,
    type: "success", // or "error" or "info" as default
    text: "",
  });
  const [isAnyApplicationSelected, setIsAnyApplicationSelected] =
    useState<boolean>(false);
  const [matchedServices, setMatchedServices] = useState<string>("");
  const [matchedNodeDetail, setMatchedDetails] = useState<any[]>([]);
  const [searchResults, setSearchbarResults] = useState<Node[] | null>(null);
  const [nodeLabel, setNodeLabel] = useState<any[]>([]);
  const [showAddNewNodeModal, setShowAddNewNodeModal] = useState<any>(null);
  const [linkedNodeDetail, setLinkedNodeDetail] = useState<any[]>([]);
  const [nodeJmId, setNodeJmlId] = useState<any[]>([]);
  const [linkedNode, setLinkedNode] = useState<any[]>([]);
  const [cloudConnection, setCloudConnection] = useState<boolean>(false);
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const [connectedAccount, setConnectedAccount] = useState<any[]>([]);
  const [disableAskAi, setDisableAskAi] = useState<boolean>(false);
  const [v2PostProcessingPending, setV2PostProcessingPending] = useState<boolean>(false);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>("");
  const [isSimpleElk, setIsSimpleElk] = useState<boolean>(false);
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [edgeForRename, setEdgeForRename] = useState<any>(null);
  const [selectNodeForLinking, setSelectNodeForLinking] = useState<any>(null);
  const isMatchRef = useRef<boolean>(false);
  const [openZoomModal, setOpenZoomModal] = useState<boolean>(false);
  const [openInfraAskAi, setOpenInfraAskAi] = useState<boolean>(false);
  const [graphNodes, setGraphNodes] = useState<CodeViewNode[]>([]);
  const [graphEdges, setGraphEdges] = useState<CodeViewEdge[]>([]);
  const [v2CanvasView, setV2CanvasView] = useState<V2CanvasViewMode>("claude");
  const [v2AiFlowIndex, setV2AiFlowIndex] = useState<number>(0);
  const [graphView, setGraphView] = useState<boolean>(false);
  const [linkedjmlId, setLinkedjmlId] = useState<any>("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isDataUpdated, setIsDataUpdated] = useState<boolean>(false);
  const codeConnectRequestIdRef = useRef<string | null>(null);
  const generateCodeRequestIdRef = useRef<string | null>(null);
  const retrieveRequestIdRef = useRef<string | null>(null);
  const inferRepoRequestIdRef = useRef<string | null>(null);
  const [infraIdRegion, setInfraIdRegion] = useState({
    account_id: "",
    account_region: "",
  });
  const showSuccessToastRef = useRef<boolean>(true);
  const autoConnectPendingRef = useRef<{
    requestId: string;
    input: Input;
  } | null>(null);
  const [groupViewAvailable, setGroupViewAvailable] = useState<boolean>(false);
  const [expandedCodeNodeIds, setExpandedCodeNodeIds] = useState<string[]>([]);
  const [showCostOnNodes, setShowCostOnNodes] = useState<boolean>(false);
  const [connectedUrls, setConnectedUrls] = useState<Record<string, string[]>>(
    {},
  );
  // Repository update tracking state
  const [repoUpdates, setRepoUpdates] = useState<RepoUpdateStatus[]>([]);
  const [diffSummary, setDiffSummary] = useState<DiffSummary | null>(null);
  const [activeDiffGraphChanges, setActiveDiffGraphChanges] =
    useState<GraphChanges | null>(null);
  const [repoScanCommits, setRepoScanCommits] = useState<RepoScanCommitInfo[]>([]);

  // Progressive pipeline phases state
  const [phase1Complete, setPhase1Complete] = useState<boolean>(false);
  const [phase2Complete, setPhase2Complete] = useState<boolean>(false);
  const [isLoadingPhase2, setIsLoadingPhase2] = useState<boolean>(false);
  const [hasPostScanCompleted, setHasPostScanCompleted] =
    useState<boolean>(false);
  const [showAddMultipleGitRepoModal, setShowAddMultipleGitRepoModal] =
    useState<boolean>(false);
  const [reconnectRepoUrl, setReconnectRepoUrl] = useState<string | null>(null);
  const [showAddConnectCloudModal, setShowAddConnectCloudModal] =
    useState<boolean>(false);
  const takeSnapShotRef = useRef<((instance?: any) => void) | null>(null);
  const screenshotSuccessfulRef = useRef<boolean>(false);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [isLoadingActivityId, setIsLoadingActivityId] = useState<boolean>(true);
  const [hasPendingActions, setHasPendingActions] = useState<boolean>(false);
  const [selectedRepo, setSelectedRepo] = useState<string | null>("Default");
  const [notifyOnCompletion, setNotifyOnCompletion] = useState<boolean>(false);

  // Chat state sharing between footer and full chat
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] =
    useState<boolean>(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [isAskAiTimeout, setIsAskAiTimeout] = useState<boolean>(false);
  const askAiTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [logoutHandler, setLogoutHandler] = useState<(() => void) | null>(null);
  const [codeRescanMode, setCodeRescanMode] = useState<CodeRescanMode | null>(null);
  const [rescanModeSupported, setRescanModeSupported] =
    useState<boolean>(false);
  const [isCompactViewDisabled, setIsCompactViewDisabled] =
    useState<boolean>(false);
  const [showReviewSidebar, setShowReviewSidebar] = useState<boolean>(false);
  const [reviewSidebarNodeId, setReviewSidebarNodeId] = useState<string | null>(null);

  /**
   *  Context Value
   */
  const context: ContextType = {
    theme: theme,
    toggleTheme: toggleTheme,
    showSpinner: showSpinner,
    openSpinnerHandler: openSpinnerHandler,
    closeSpinnerHandler: closeSpinnerHandler,
    activeSection,
    setActiveSection,
    highlightEdgesContext,
    setHighlightEdgesContext,
    highlightNodesContext,
    setHighlightNodesContext,
    searchNode,
    setSearchNode,
    showBars,
    setShowBars,
    footerSearchText,
    setFooterSearchText,
    useCache,
    setUseCache,
    projects,
    setProjects,
    footerBtn,
    setFooterBtn,
    showDocSidebarCanvas,
    setDocSidebarCanvas,
    isTourOpen,
    setIsTourOpen,
    edgeColor,
    setEdgeColor,
    zoomVal,
    setZoomVal,
    userId,
    setUserId,
    showToastr,
    setShowToastr,
    isAnyApplicationSelected,
    setIsAnyApplicationSelected,
    queryStr,
    setQueryStr,
    selectedInfraviewDetail,
    setSelectedInfraviewDetail,
    setMatchedServices,
    matchedServices,
    matchedNodeDetail,
    setMatchedDetails,
    searchResults,
    setSearchbarResults,
    nodeLabel,
    setNodeLabel,
    showAddNewNodeModal,
    setShowAddNewNodeModal,
    linkedNodeDetail,
    setLinkedNodeDetail,
    nodeJmId,
    setNodeJmlId,
    linkedNode,
    setLinkedNode,
    cloudConnection,
    setCloudConnection,
    buttonClicked,
    setButtonClicked,
    connectedAccount,
    setConnectedAccount,
    disableAskAi,
    setDisableAskAi,
    v2PostProcessingPending,
    setV2PostProcessingPending,
    reactFlowInstance,
    setReactFlowInstance,
    isSimpleElk,
    setIsSimpleElk,
    isRenaming,
    setIsRenaming,
    edgeForRename,
    setEdgeForRename,
    selectNodeForLinking,
    setSelectNodeForLinking,
    isMatchRef,
    openZoomModal,
    setOpenZoomModal,
    openInfraAskAi,
    setOpenInfraAskAi,
    graphNodes,
    setGraphNodes,
    graphEdges,
    setGraphEdges,
    v2CanvasView,
    setV2CanvasView,
    v2AiFlowIndex,
    setV2AiFlowIndex,
    graphView,
    setGraphView,
    setLinkedjmlId,
    linkedjmlId,
    editMode,
    setEditMode,
    isDataUpdated,
    setIsDataUpdated,
    codeConnectRequestIdRef: codeConnectRequestIdRef,
    generateCodeRequestIdRef: generateCodeRequestIdRef,
    retrieveRequestIdRef: retrieveRequestIdRef,
    inferRepoRequestIdRef: inferRepoRequestIdRef,
    infraIdRegion,
    setInfraIdRegion,
    showSuccessToastRef,
    autoConnectPendingRef,
    groupViewAvailable,
    setGroupViewAvailable,
    expandedCodeNodeIds,
    setExpandedCodeNodeIds,
    showCostOnNodes,
    setShowCostOnNodes,
    connectedUrls,
    setConnectedUrls,
    // Repository update tracking
    repoUpdates,
    setRepoUpdates,
    diffSummary,
    setDiffSummary,
    activeDiffGraphChanges,
    setActiveDiffGraphChanges,
    repoScanCommits,
    setRepoScanCommits,
    phase1Complete,
    setPhase1Complete,
    phase2Complete,
    setPhase2Complete,
    isLoadingPhase2,
    setIsLoadingPhase2,
    hasPostScanCompleted,
    setHasPostScanCompleted,
    showAddMultipleGitRepoModal,
    setShowAddMultipleGitRepoModal,
    reconnectRepoUrl,
    setReconnectRepoUrl,
    showAddConnectCloudModal,
    setShowAddConnectCloudModal,
    // Chat state sharing between footer and full chat
    chatMessages,
    setChatMessages,
    currentThreadId,
    setCurrentThreadId,
    pendingQuestion,
    setPendingQuestion,
    isWaitingForResponse,
    setIsWaitingForResponse,
    currentRequestId,
    setCurrentRequestId,
    takeSnapShotRef,
    screenshotSuccessfulRef,
    activityId,
    setActivityId,
    isLoadingActivityId,
    setIsLoadingActivityId,
    hasPendingActions,
    setHasPendingActions,
    selectedRepo,
    setSelectedRepo,
    isAskAiTimeout,
    setIsAskAiTimeout,
    askAiTimerRef,
    notifyOnCompletion,
    setNotifyOnCompletion,
    codeRescanMode,
    setCodeRescanMode,
    rescanModeSupported,
    setRescanModeSupported,
    isCompactViewDisabled,
    setIsCompactViewDisabled,
    showReviewSidebar,
    setShowReviewSidebar,
    reviewSidebarNodeId,
    setReviewSidebarNodeId,
    logoutHandler,
    setLogoutHandler,
  };

  (window as any).context = context;

  return (
    <CreateContext.Provider value={context}>
      {props.children}
    </CreateContext.Provider>
  );
};

export default ContextProvider;
